<template>
    <v-dialog :width="600" v-model="dialogModel" persistent>

        <toolbar-page :title="creating ? 'Create new recruitment source' : 'Edit recruitment source'"
                      disable-icon
                      help="recruitment-sources-edit-dialog">
            <template slot="buttons">
                <v-tooltip top color="primary">
                    <template v-slot:activator="{ on }">
                        <v-btn icon v-on="on" @click="closeDialog">
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                    </template>
                    <span>Close</span>
                </v-tooltip>
            </template>
        </toolbar-page>

        <v-card v-if="dialogModel">

            <v-container fluid class="pa-4">

                <v-row dense>
                    <v-col cols="12" class="pt-1">
                        <v-alert prominent type="info" :value="true" class="mt-0 body-2">
                            Enter recruitment source cost in reward points.
                            For example if $1 equals to 100 points and your recruitment cost is 50c
                            you would enter 50 as the cost per conversion
                        </v-alert>

                    </v-col>
                </v-row>
                <validation-observer ref="validationObserver" v-slot="{ invalid }">
                    <v-row dense>

                        <v-col cols="12" class="pt-0">

                            <validation-provider name="name"
                                                 mode="lazy"
                                                 rules="required|min:2|max:150"
                                                 v-slot="{errors}">
                                <v-text-field
                                    v-model="form.data.name"
                                    label="Name"
                                    :error-messages="errors ? errors : form.getError('name')"
                                    @keydown.enter="enterDown"
                                ></v-text-field>

                            </validation-provider>
                        </v-col>

                        <v-col cols="6">

                            <validation-provider name="cost per conversion"
                                                 mode="lazy"
                                                 rules="required|integer|min_value:0|max_value:10000"
                                                 v-slot="{errors}">
                                <v-text-field
                                    v-model="form.data.cost_per_conversion"
                                    label="Cost per conversion"
                                    :error-messages="errors ? errors : form.getError('cost_per_conversion')"
                                    @keydown.enter="enterDown"
                                ></v-text-field>
                            </validation-provider>

                        </v-col>

                        <v-col cols="1">

                        </v-col>

                        <!-- ENABLE DISABLE SOURCE -->
                        <v-col cols="5" class="mt-2">

                            <v-select v-model="form.data.enabled"
                                      :items="onOffItems"
                                      :disabled="form.data.id === 1"
                                      item-text="name"
                                      solo
                                      color="white"
                                      :background-color="form.data.enabled ? 'green darken-2' : 'red darken-2'"
                                      hide-details
                                      item-value="value">
                            </v-select>

                        </v-col>

                        <v-col cols="12">

                            <v-card :color="form.data.tune_enabled ? 'grey darken-2' : 'transparent'" flat class="pl-4 pr-4 pt-1">

                                <v-switch
                                          color="primary"
                                          v-model="form.data.tune_enabled"
                                          label="Enable TUNE conversion reporting"
                                ></v-switch>

                                <validation-provider name="tune server"
                                                     mode="lazy"
                                                     rules="url|min:2|max:150"
                                                     v-slot="{errors}">
                                    <v-scale-transition>

                                        <v-text-field
                                            class="mt-2"
                                            v-show="form.data.tune_enabled"
                                            v-model="form.data.tune_server_address"
                                            label="Tracking server address"
                                            :error-messages="errors ? errors : form.getError('tune_server_address')"
                                            @keydown.enter="enterDown"
                                            prepend-icon="mdi-link"
                                        ></v-text-field>
                                    </v-scale-transition>

                                </validation-provider>

                            </v-card>

                            <div class="d-flex justify-space-between pt-3">
                                <v-btn :loading="saving" v-if="isUpdating" @click="update" color="primary">UPDATE</v-btn>
                                <v-btn :loading="saving" v-else color="primary" @click="create">SAVE</v-btn>
                                <v-btn color="secondary" @click="closeDialog">CANCEL</v-btn>
                            </div>

                        </v-col>

                    </v-row>
                </validation-observer>
            </v-container>

        </v-card>

        <json v-if="false" :data="form"/>

    </v-dialog>
</template>

<script>

import Form from '../../../components/forms/Form';
import ToolbarPage from '../../../components/UI/ToolbarPage';
import {extend} from 'vee-validate/dist/vee-validate.full';
import Json from 'vue-json-pretty';

const vuexModule = 'RecruitmentSources';

export default {

    components: {
        ToolbarPage,
        Json
    },

    props: {
        value: {
            required: true,
            type: Boolean,
        },
        source: {
            required: true,
            type: Object | null,
        }
    },

    watch: {
        value(dialogOpened) {
            if (dialogOpened) {
                this.prepareForm();
            }
        }
    },

    mounted() {

        this.prepareForm();

        extend('url', {
            validate(value) {

                try {
                    const parsedUrl = new URL(value);
                    return new RegExp('^https:\/\/[0-9a-z-.]+\.[a-z]+$').test(parsedUrl.origin);
                } catch (e) {
                    return false;
                }

            },
            message: "Valid URL is required and must begin with https://"
        });
    },

    data() {
        return {
            form: null,
            isUpdating: null,
            saving: false,
            cost_per_conversion: null,
            onOffItems: [
                {
                    name: 'Source ENABLED',
                    value: true,
                },
                {
                    name: 'Source DISABLED',
                    value: false
                }
            ]
        }
    },

    computed: {

        dialogModel: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit('input', value);
            }
        },

        recruitmentSource: {
            get() {
                return this.source;
            },
            set(source) {
                this.$emit('change', source);
            }
        },

        creating() {
            return !this.form.data.id;
        }
    },

    methods: {

        prepareForm() {

            this.createForm();

            if (this.source) {
                this.form = new Form(
                    this.source,
                    [
                        'id',
                        'name',
                        'enabled',
                        'cost_per_conversion',
                        'conversion_count',
                        'tune_enabled',
                        'tune_server_address'
                    ]
                );
            }

            this.isUpdating = !!this.form.data.id;
        },

        update() {

            this.$refs.validationObserver.validate()
                .then(isValid => {
                    if (isValid) {

                        if (this.form.data.tune_enabled && this.form.data.tune_server_address.length < 10) {
                            Bus.$emit('flash-message', {type: 'error', message: 'Tune server address must be provided if enabled!'});
                            return;
                        }

                        this.saving = true;

                        this.$store.dispatch(vuexModule + '/update', this.form)
                            .then(() => {
                                Bus.$emit('flash-message', {message: 'Recruitment source updated...'});
                                this.$emit('save');
                            })
                            .catch((error) => {

                                if (error.status === 422) {
                                    Bus.$emit('flash-message', {
                                        type: 'error',
                                        message: 'Form contains errors!'
                                    });
                                } else {
                                    Bus.$emit('server-error');
                                }
                            })
                            .finally(() => {
                                this.saving = false;
                                this.closeDialog();
                            })
                    } else {
                        Bus.$emit('flash-message', {type: 'error', message: 'Form contains validation errors'});
                    }
                });
        },

        create() {

            this.$refs.validationObserver.validate()
                .then(isValid => {
                    if (isValid) {
                        this.saving = true;

                        this.$store.dispatch(vuexModule + '/create', this.form)
                            .then(() => {
                                Bus.$emit('flash-message', {message: 'New recruitment source created...'});
                                this.$emit('save');
                            })
                            .catch((error) => {

                                if (error.status === 422) {
                                    Bus.$emit('flash-message', {
                                        type: 'error',
                                        message: 'Form contains errors!'
                                    });
                                } else {
                                    Bus.$emit('server-error');
                                }
                            })
                            .finally(() => {
                                this.saving = false;
                                this.closeDialog();
                            })
                    } else {
                        Bus.$emit('flash-message', {type: 'error', message: 'Form contains validation errors'});
                    }
                });
        },

        enterDown() {
            if (this.form.data.id) {
                this.update();
            } else {
                this.create();
            }
        },

        closeDialog() {
            this.$emit('input', false);
        },

        createForm() {
            this.form = new Form({
                cost_per_conversion: null,
                enabled: true,
                name: null,
                tune_enabled: false,
                tune_server_address: null,
            });
        }
    },

    created() {

        this.createForm();

        if (!this.$store.state[vuexModule]) {
            this.$store.registerModule(vuexModule, Store);
        }
    }
}
</script>
